import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import Tasks from "./Tasks";
import Visits from "./Visits";
import PatientCases from "./PatientCases";
import { CommonStyle } from "Components";
import CstmBtn from "Components/CstmBtn";
import { SideModalProps } from "DataTypes/Modal";
import ImgPath from "Constant/Imgs";
import TabPanel from "Components/TabPanel";
import RedoxApi from "Service/Redox.api";
import RedoxFormatter from "Utils/Redox.Formatter";
import { VisitSearchType } from "DataTypes/Services/Redox.type";
import PatientApi from "Service/Patient.api";
import EhrApi from "Service/Ehr.api";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import TaskFormatter from "Utils/Task.Formatter";
import { TaskListType } from "DataTypes/Services/Tasks";
import { PatientCaseListType } from "./Data";
import { label } from "Constant/En";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import { EhrDomains } from "Constant/Ehr";

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const TASKS_TAB = 0;
const VISITS_TAB = 1;
const CASES_TAB = 2;

const TaskVisit: FC<SideModalProps> = ({ onClose }) => {
  const { patientInfo, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const patId = patientInfo.id;
  const [visitData, setVisitData] = useState<VisitSearchType[]>([]);
  const [rows, setRows] = useState<TaskListType[]>([]);
  const [cases, setCases] = useState<PatientCaseListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(TASKS_TAB);
  // Cases are an EHR domain of their own: Athena serves them and reports them in
  // capabilities, so the tab and its sync appear exactly where the vendor has them.
  const casesSync = useEhrSync(EhrDomains.CASES);
  const showCases = Boolean(ehr?.vendor);

  useEffect(() => {
    if (tabValue === TASKS_TAB) getTaskData();
    else if (tabValue === VISITS_TAB) getVisitData();
    else getCaseData();
  }, [tabValue, patId]);

  const getVisitData: () => void = async () => {
    setLoading(true);
    const res = await RedoxApi.VisitSearch({ id1: patId });
    if (res?.success) {
      setVisitData(RedoxFormatter.VisitSearch(res?.data));
    }
    setLoading(false);
  };

  const getTaskData: () => void = async () => {
    setLoading(true);
    const res = await PatientApi.PatientTaskSearch({
      id1: patId,
      offset: 0,
      limit: 100,
    });
    if (res?.success) setRows(TaskFormatter.list(res?.data));
    setLoading(false);
  };

  const getCaseData: () => void = async () => {
    setLoading(true);
    const res = await EhrApi.searchPatientCases({ id1: patId });
    // The integrator answers { patientIdentifier, totalCount, cases }. The table keys rows by
    // `id` and shows `dateTime`, neither of which the vendor payload carries under that name.
    const list = Array.isArray(res?.data?.cases) ? res.data.cases : [];
    setCases(
      list.map((el: any) => ({
        ...el,
        id: `${el?.patientCaseId || el?.internalCaseId || ""}`,
        dateTime: `${el?.caseCreatedDate || ""}`.replace(/\.\d+$/, ""),
      }))
    );
    setLoading(false);
  };

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Tasks & Visits</Typography>
        {/* Controls on one line, with the sync stamp beneath them:
            stacking it under the button alone makes that column taller
            and drops the icons out of line with it. */}
        <Box
          sx={{
            ...CommonStyle.sxFlexDirectionColumn,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ ...CommonStyle.sxRow }}>
            {tabValue === CASES_TAB && casesSync.canSync && (
              <Tooltip arrow placement="top" title={casesSync.title}>
                <span>
                  <CstmBtn
                    label={label.syncFromEhr}
                    btnType="primary"
                    loading={casesSync.syncing}
                    loadingText="Syncing..."
                    onClick={async () => {
                      await casesSync.handleSync();
                      getCaseData();
                    }}
                    sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                  />
                </span>
              </Tooltip>
            )}
            <Box sx={{ ...CommonStyle.sxRoundIconsMainSm }} onClick={onClose}>
              <ImgPath.CloseIcon titleAccess="Close" />
            </Box>
          </Box>
          {tabValue === CASES_TAB && <EhrLastSync sync={casesSync} />}
        </Box>
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Tasks" value={TASKS_TAB} {...a11yProps(TASKS_TAB)} />
          <Tab label="Visits" value={VISITS_TAB} {...a11yProps(VISITS_TAB)} />
          {showCases && (
            <Tab label="Cases" value={CASES_TAB} {...a11yProps(CASES_TAB)} />
          )}
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={TASKS_TAB} style={{ ...sxTabPanel }}>
        <Tasks data={rows} loading={loading} onClose={onClose} />
      </TabPanel>
      <TabPanel value={tabValue} index={VISITS_TAB} style={{ ...sxTabPanel }}>
        <Visits data={visitData} loading={loading} />
      </TabPanel>
      {showCases && (
        <TabPanel value={tabValue} index={CASES_TAB} style={{ ...sxTabPanel }}>
          <PatientCases data={cases} loading={loading} />
        </TabPanel>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "100%",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
    </Fragment>
  );
};
export default TaskVisit;

const sxTab = {
  flexShrink: 0,
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxTabPanel = {
  flex: 1,
  minHeight: 0,
};
